import React, { useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer, ScaleControl, CircleMarker, Tooltip, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { CountyBordersLayer } from "./env_util";
import StationLineChart from "./StationLineChart";
import {
    buildStations,
    DATES,
    KBDI_SUMMARY,
    IS_PLACEHOLDER_DATA,
    VALIDATION_RAWS_IDS,
    getStationSeries,
    type StationMeta,
} from "./stationAnalysisData";
import { useStationLocations, type Network, type StationLocation } from "./stationLocations";
import "./StationAnalysis.css";

type ColorMetric = "tmaxBias" | "kbdiPT";

const METRICS: Record<ColorMetric, { label: string; unit: string; limit: number; get: (s: StationMeta) => number }> = {
    tmaxBias: { label: "Tmax bias (grid − station)", unit: "°F", limit: 4, get: s => s.tmaxBias },
    kbdiPT: { label: "KBDI mean diff, precip + Tmax from grid", unit: "", limit: 150, get: s => s.kbdi.pt },
};

const COLORS = {
    station: "#1e3a8a",
    grid: "#dc2626",
    p: "#1f77b4",
    t: "#d62728",
    pt: "#7b3fa0",
};

// Diverging blue → white → red. Negative = grid lower than station.
const NEG: [number, number, number] = [37, 99, 235];
const MID: [number, number, number] = [245, 245, 245];
const POS: [number, number, number] = [220, 38, 38];
const divergingColor = (value: number, limit: number) => {
    const t = Math.max(-1, Math.min(1, value / limit));
    const [a, b] = t < 0 ? [MID, NEG] : [MID, POS];
    const k = Math.abs(t);
    const c = a.map((v, i) => Math.round(v + (b[i] - v) * k));
    return `rgb(${c[0]}, ${c[1]}, ${c[2]})`;
};

// Context networks: no validation data, shown for reference only.
type ContextLayer = "otherRaws" | "fawn" | "asos";
const CONTEXT_LAYER_KEYS: ContextLayer[] = ["otherRaws", "fawn", "asos"];
const CONTEXT_STYLE: Record<ContextLayer, { label: string; color: string }> = {
    otherRaws: { label: "Other RAWS", color: "#64748b" },
    fawn: { label: "FAWN", color: "#16a34a" },
    asos: { label: "ASOS", color: "#7c3aed" },
};

const NETWORK_LABEL: Record<Network, string> = { RAWS: "RAWS", FAWN: "FAWN", ASOS: "ASOS" };

const fmtSigned = (v: number, digits = 1) => `${v > 0 ? "+" : ""}${v.toFixed(digits)}`;

const DEFAULT_CENTER: [number, number] = [27.8, -83.0];

const FlyToStation: React.FC<{ station: StationMeta | null }> = ({ station }) => {
    const map = useMap();
    useEffect(() => {
        if (station) map.flyTo([station.lat, station.lon], Math.max(map.getZoom(), 8), { duration: 0.6 });
    }, [station, map]);
    return null;
};

const StationAnalysis: React.FC = () => {
    const { data: locations, error: locationsError } = useStationLocations();
    const stations = useMemo(() => (locations ? buildStations(locations.raws) : []), [locations]);
    const contextStations = useMemo<Record<ContextLayer, StationLocation[]>>(() => ({
        otherRaws: locations?.raws.filter(r => !VALIDATION_RAWS_IDS.has(r.id)) ?? [],
        fawn: locations?.fawn ?? [],
        asos: locations?.asos ?? [],
    }), [locations]);

    const [metric, setMetric] = useState<ColorMetric>("tmaxBias");
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [showCounties, setShowCounties] = useState(true);
    const [contextLayers, setContextLayers] = useState<Record<ContextLayer, boolean>>({
        otherRaws: false,
        fawn: false,
        asos: false,
    });
    const toggleContext = (key: ContextLayer, on: boolean) => setContextLayers(prev => ({ ...prev, [key]: on }));
    const activeContext = CONTEXT_LAYER_KEYS.filter(k => contextLayers[k]);

    const metricCfg = METRICS[metric];
    const selected = stations.find(s => s.id === selectedId) ?? null;
    const series = useMemo(() => (selected ? getStationSeries(selected) : null), [selected]);

    const sortedStations = useMemo(
        () => [...stations].sort((a, b) => metricCfg.get(a) - metricCfg.get(b)),
        [stations, metricCfg]
    );

    return (
        <div className="st-root">
            {IS_PLACEHOLDER_DATA && (
                <div className="st-placeholder-note" role="note">
                    <strong>Placeholder data.</strong> No backend endpoint exists yet. Station locations come from the
                    RAWS, FAWN and ASOS location files. Station bias, MAE and the northern stations’ KBDI means are
                    copied from the Jan–Sep 2026 analysis; southern KBDI values and every daily time series are
                    synthetic stand-ins.
                </div>
            )}

            {locationsError && (
                <div className="st-error-note" role="alert">
                    Station locations could not be loaded: {locationsError}. Check that the CSV files are in the
                    stations folder and reload the page.
                </div>
            )}

            <div className="env-controls-row">
                <div className="st-segmented" role="radiogroup" aria-label="Color stations by">
                    {(Object.keys(METRICS) as ColorMetric[]).map(key => (
                        <button
                            key={key}
                            type="button"
                            role="radio"
                            aria-checked={metric === key}
                            className={`st-segment${metric === key ? " st-segment-active" : ""}`}
                            onClick={() => setMetric(key)}
                        >
                            {key === "tmaxBias" ? "Tmax bias" : "KBDI difference"}
                        </button>
                    ))}
                </div>
                <div className="env-layer-toggles">
                    <label className="env-toggle">
                        <span className="env-toggle-switch">
                            <input type="checkbox" checked={showCounties} onChange={e => setShowCounties(e.target.checked)} />
                            <span className="env-toggle-thumb" />
                        </span>
                        Counties
                    </label>
                    {CONTEXT_LAYER_KEYS.map(key => (
                        <label key={key} className="env-toggle">
                            <span className="env-toggle-switch">
                                <input
                                    type="checkbox"
                                    checked={contextLayers[key]}
                                    onChange={e => toggleContext(key, e.target.checked)}
                                />
                                <span className="env-toggle-thumb" />
                            </span>
                            {CONTEXT_STYLE[key].label}
                        </label>
                    ))}
                </div>
            </div>

            <div className="st-layout">
                <div className="env-map-wrapper st-map-wrapper">
                    {!locations && !locationsError && <div className="env-map-loading">Loading stations...</div>}
                    <MapContainer center={DEFAULT_CENTER} zoom={7} scrollWheelZoom className="env-map">
                        <TileLayer
                            attribution="&copy; OpenStreetMap contributors"
                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        />
                        <ScaleControl position="bottomleft" />
                        {showCounties && <CountyBordersLayer />}
                        <FlyToStation station={selected} />

                        {activeContext.flatMap(key => {
                            const layer = CONTEXT_STYLE[key];
                            return contextStations[key].map(st => (
                                <CircleMarker
                                    key={`${st.network}-${st.id}`}
                                    center={[st.lat, st.lon]}
                                    radius={4}
                                    pathOptions={{ color: "#fff", weight: 1, fillColor: layer.color, fillOpacity: 0.9 }}
                                >
                                    <Tooltip direction="top" offset={[0, -4]}>
                                        {NETWORK_LABEL[st.network]} {st.network === "ASOS" ? st.id : `${st.name} (${st.id})`}
                                        {st.elevation != null ? `, ${Math.round(st.elevation)} m` : ""}. No validation data.
                                    </Tooltip>
                                </CircleMarker>
                            ));
                        })}

                        {stations.map(s => {
                            const value = metricCfg.get(s);
                            const isSel = s.id === selectedId;
                            return (
                                <CircleMarker
                                    key={s.id}
                                    center={[s.lat, s.lon]}
                                    radius={isSel ? 11 : 8}
                                    pathOptions={{
                                        fillColor: divergingColor(value, metricCfg.limit),
                                        fillOpacity: 0.95,
                                        color: isSel ? "#111827" : "#475569",
                                        weight: isSel ? 3 : 1.5,
                                        dashArray: metric === "kbdiPT" && s.kbdiEstimated ? "3 3" : undefined,
                                    }}
                                    eventHandlers={{ click: () => setSelectedId(s.id) }}
                                >
                                    <Tooltip direction="top" offset={[0, -8]}>
                                        {s.id} {s.name}: {fmtSigned(value, metric === "tmaxBias" ? 1 : 0)}{metricCfg.unit}
                                        {metric === "kbdiPT" && s.kbdiEstimated ? " (estimated)" : ""}
                                    </Tooltip>
                                </CircleMarker>
                            );
                        })}
                    </MapContainer>

                    <div className="st-map-legend">
                        <div className="st-map-legend-title">{metricCfg.label}</div>
                        <div
                            className="st-map-legend-bar"
                            style={{
                                background: `linear-gradient(to right, ${divergingColor(-metricCfg.limit, metricCfg.limit)}, ${divergingColor(0, metricCfg.limit)}, ${divergingColor(metricCfg.limit, metricCfg.limit)})`,
                            }}
                        />
                        <div className="st-map-legend-ticks">
                            <span>−{metricCfg.limit}{metricCfg.unit}</span>
                            <span>0</span>
                            <span>+{metricCfg.limit}{metricCfg.unit}</span>
                        </div>
                        {metric === "kbdiPT" && <div className="st-map-legend-note">Dashed outline: estimated value</div>}
                        {activeContext.length > 0 && (
                            <div className="st-map-legend-networks">
                                {activeContext.map(key => (
                                    <span key={key} className="st-map-legend-network">
                                        <span className="st-dot st-dot-small" style={{ background: CONTEXT_STYLE[key].color }} />
                                        {CONTEXT_STYLE[key].label} ({contextStations[key].length})
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                <aside className="st-list" aria-label="Stations">
                    <div className="st-list-header">
                        {stations.length} FEMS stations, sorted by {metric === "tmaxBias" ? "Tmax bias" : "KBDI difference"}
                    </div>
                    <ul>
                        {sortedStations.map(s => {
                            const value = metricCfg.get(s);
                            return (
                                <li key={s.id}>
                                    <button
                                        type="button"
                                        className={`st-list-row${s.id === selectedId ? " st-list-row-active" : ""}`}
                                        onClick={() => setSelectedId(s.id)}
                                    >
                                        <span className="st-dot" style={{ background: divergingColor(value, metricCfg.limit) }} />
                                        <span className="st-list-name">
                                            <span className="st-list-id">{s.id}</span> {s.name}
                                        </span>
                                        <span className="st-list-value">
                                            {fmtSigned(value, metric === "tmaxBias" ? 1 : 0)}
                                            {metric === "kbdiPT" && s.kbdiEstimated ? "*" : ""}
                                        </span>
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                </aside>
            </div>

            {selected && series ? (
                <section className="env-about st-detail">
                    <div className="st-detail-head">
                        <div>
                            <h2 className="env-about-title">{selected.id} {selected.name}</h2>
                            <p className="st-detail-sub">
                                RAWS {selected.rawsId}
                                {selected.agency ? `, ${selected.agency}` : ""}, {selected.lat.toFixed(3)}°N {Math.abs(selected.lon).toFixed(3)}°W
                                {selected.elevation != null ? `, ${Math.round(selected.elevation)} m` : ""}. Jan 1 – Sep 30, 2026
                            </p>
                        </div>
                        <button type="button" className="env-back-button" onClick={() => setSelectedId(null)}>
                            Show all-station summary
                        </button>
                    </div>

                    <dl className="st-stats">
                        <div><dt>Tmax bias</dt><dd>{fmtSigned(selected.tmaxBias)} °F</dd></div>
                        <div><dt>Tmax MAE</dt><dd>{selected.tmaxMae.toFixed(1)} °F</dd></div>
                        <div><dt>KBDI diff, precip</dt><dd>{fmtSigned(selected.kbdi.p, 0)}</dd></div>
                        <div><dt>KBDI diff, Tmax</dt><dd>{fmtSigned(selected.kbdi.t, 0)}</dd></div>
                        <div><dt>KBDI diff, both</dt><dd>{fmtSigned(selected.kbdi.pt, 0)}</dd></div>
                    </dl>
                    {selected.kbdiEstimated && (
                        <p className="st-detail-warning">
                            KBDI numbers for this station are estimated placeholders; the analysis for southern stations was not provided.
                        </p>
                    )}

                    <h3 className="st-chart-title">Daily maximum temperature, station vs FNN grid</h3>
                    <StationLineChart
                        dates={DATES}
                        unit="°F"
                        yLabel="°F"
                        series={[
                            { name: "Station (FW21)", color: COLORS.station, values: series.tmaxStation },
                            { name: "FNN grid", color: COLORS.grid, values: series.tmaxGrid },
                        ]}
                    />

                    <h3 className="st-chart-title">KBDI with inputs from the FNN grid, minus the all-FEMS run</h3>
                    <p className="st-chart-caption">0 means the grid input has no effect. Negative means the grid KBDI is lower (wetter) than the FEMS KBDI.</p>
                    <StationLineChart
                        dates={DATES}
                        yLabel="KBDI diff"
                        zeroLine
                        series={[
                            { name: "Precip from grid", color: COLORS.p, values: series.kbdiP },
                            { name: "Tmax from grid", color: COLORS.t, values: series.kbdiT },
                            { name: "Precip + Tmax from grid", color: COLORS.pt, values: series.kbdiPT },
                        ]}
                    />
                    <p className="st-chart-caption st-chart-caption-muted">Daily series are synthetic placeholders.</p>
                </section>
            ) : (
                <section className="env-about st-detail">
                    <h2 className="env-about-title">FNN grid vs FEMS stations: KBDI drivers</h2>
                    <p className="env-about-desc">
                        Select a station on the map or in the list to compare its daily Tmax with the grid and see how
                        swapping each KBDI input for the grid value changes the result. The baseline (0) uses Tmax and
                        precipitation from FEMS for both.
                    </p>

                    <div className="st-table-wrap">
                        <table className="st-table">
                            <caption>Median difference from the all-FEMS run</caption>
                            <thead>
                                <tr><th scope="col">Inputs from the grid</th><th scope="col">Bias</th><th scope="col">MAE</th></tr>
                            </thead>
                            <tbody>
                                {KBDI_SUMMARY.map(r => (
                                    <tr key={r.inputs}>
                                        <th scope="row">{r.inputs}</th>
                                        <td>{fmtSigned(r.bias)}</td>
                                        <td>{r.mae.toFixed(1)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <p className="env-about-desc">
                        Precipitation is the larger effect at 16 of 28 stations. It arrives in jumps at rain events: the
                        grid’s rain total is a median 14% above the station’s, spread over many rain days. Tmax drifts
                        steadily, with the grid running cooler than the station almost everywhere. The two effects are
                        close to additive and push the same way, and the combined error is larger than the sum because
                        of the drying calculation, so both inputs need correcting.
                    </p>
                </section>
            )}
        </div>
    );
};

export default StationAnalysis;